
import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { Mail } from 'lucide-react';
import { ContactFormData } from '@/api/contact';

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare the data for the API call
      const contactData: ContactFormData = {
        ...formData,
        language
      };
      
      // Make API call to the server endpoint
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: t('contact.form.success'),
          description: language === 'en' 
            ? "We'll get back to you as soon as possible." 
            : "Nous vous répondrons dès que possible.",
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: language === 'en' ? 'Error' : 'Erreur',
        description: language === 'en' 
          ? "There was an error sending your message. Please try again." 
          : "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer.",
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email subject based on language
  const emailSubject = language === 'en' ? 'Contact Inquiry from Website' : 'Demande de contact depuis le site web';
  const emailBody = language === 'en' ? 'Hello, I would like more information about your services.' : 'Bonjour, je souhaiterais plus d\'informations sur vos services.';
  const mailtoLink = `mailto:info@attaboyintelligence.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  return (
    <section className="py-24 bg-gray-50" id="contact">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins text-center">{t('contact.title')}</h2>
          <p className="text-xl text-gray-600 mb-12 text-center">{t('contact.subtitle')}</p>

          <div className="flex justify-center">
            <Button
              className="cta-button py-6 px-8 text-lg"
              onClick={() => window.location.href = mailtoLink}
            >
              <Mail className="mr-2 h-5 w-5" /> 
              {language === 'en' ? 'Email Us' : 'Contactez-nous par email'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
